import { Box, Stack, Typography } from '@mui/material';
import {
  Breadcrumbs,
  Accordion,
  PositionedMenu,
  NoContent,
  GridItem,
  Button,
  GridContainer,
  ResyncDetails,
} from '../../../components/shared';
import { NavigateNext, FactoryOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useGetModemDetailsByIdQuery } from '../../../services/Modems/ModemsService';
import React, { useState, useEffect, useMemo } from 'react';
import ModemDetailAccordion from './ModemDetailAccordion';
import useStyles from './style';
import { useNavigate } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import AssignNewAsset from './AssignNewAsset';
import AssetDetails from './AssetDetails';
import { MESSAGE } from '../../../constants/message';
import ConfirmationModal from '../../../components/ConfirmationModel';
import { useDeleteModemByIdMutation } from '../../../services/Modems/ModemsService';
import Modal from '../../../components/shared/Modal';
import ModemHistoryModel from '../ModemHistoryModel/ModemHistoryModel';

const ModemDetails = () => {
  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();
  const [deleteModemApi] = useDeleteModemByIdMutation();
  const [isAssignNewMachine, setIsAssignNewMachine] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [selectedModemId, setSelectedModemId] = useState(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const {
    MODEM: { MODEM, MODEM_DETAIL },
  } = BREADCRUMB_NAMES;

  const {
    isLoading,
    isFetching,
    currentData: currentModemDetails,
    refetch,
  } = useGetModemDetailsByIdQuery({ id: Number(params.id) });

  useEffect(() => {
    const timer = setTimeout(() => {
      refetch();
    }, 100);

    return () => clearTimeout(timer);
  }, [refetch]);

  const breadcrumbData = [{ text: MODEM, redirection: 'modems' }, { text: MODEM_DETAIL }];

  const { data: modemDetailsResponse } = currentModemDetails ?? {};
  const modemResyncData = useMemo(() => {
    const resync = modemDetailsResponse?.resync;

    if (!resync) return [];

    return Array.isArray(resync) ? resync.filter(Boolean) : Object.values(resync).filter(Boolean);
  }, [modemDetailsResponse?.resync]);

  const onDeleteModalClickHandler = (value) => {
    if (value === MESSAGE.DELETE) {
      setShowDeleteModal(false);
      deleteHandler();
    } else {
      setShowDeleteModal(false);
    }
  };

  const deleteHandler = () => {
    deleteModemApi(params.id).then((response) => {
      if (response.data) {
        setShowDeleteModal(false);
        setTimeout(() => {
          navigate(`/${routeConstants.MODEMS_ROUTE}`, {
            redirect: true,
          });
        }, 1000);
      }
    });
  };

  const handleOpenHistoryModal = async (modemId) => {
    setIsHistoryLoading(true);
    setSelectedModemId(modemId);

    setTimeout(() => {
      setHistoryModal(true);
      setIsHistoryLoading(false);
    }, 200);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModal(false);
    setSelectedModemId(null);
  };

  return (
    <React.Fragment>
      <Box className={classes.breadcrumbContainer}>
        <Breadcrumbs
          icon={<NavigateNext fontSize="medium" />}
          variant="body2"
          underline="hover"
          data={breadcrumbData}
        />
      </Box>
      <Box mb={4} mt={4}>
        <Stack direction={'row'} spacing={2} alignItems="center">
          <Typography variant="h1" data-testid="modemTitle">
            {`${modemDetailsResponse?.type || ''} Modem` || 'N/A'}
          </Typography>
        </Stack>
      </Box>
      <Box mb={3} className={classes.machineDetailContent}>
        <Accordion
          defaultExpanded
          marginBottom={3}
          customizedTitle
          title={
            <Box ml={2} width="100%" display="flex" alignItems="center" justifyContent="space-between">
              <Typography data-testid="accordionTitle" variant="h2">
                Modem Details
              </Typography>
              <Box display="flex" alignItems="center">
                {modemResyncData.length > 0 && <ResyncDetails resyncData={modemResyncData} onRefresh={refetch} />}
                <PositionedMenu
                  menuItemData={[
                    {
                      btnName: 'Edit',
                      btnClickFunc: () =>
                        navigate(
                          `/${routeConstants.MODEMS_ROUTE}/${routeConstants.EDIT_MODEM_ROUTE}/${modemDetailsResponse.modemId}`,
                          {
                            state: { type: 'fromDetails' },
                          },
                        ),
                    },
                    {
                      btnName: 'History',
                      btnClickFunc: () => handleOpenHistoryModal(modemDetailsResponse.modemId),
                    },
                  ]}
                />
              </Box>
            </Box>
          }
          content={
            isLoading || isFetching ? (
              <LoadingSpinner containerHeight={400} />
            ) : (
              <ModemDetailAccordion modemInfo={modemDetailsResponse} />
            )
          }
        />
      </Box>
      <Box mb={3} className={classes.modemDetailContent}>
        {modemDetailsResponse?.asset && !isAssignNewMachine && (
          <AssetDetails
            assetId={modemDetailsResponse?.assetId}
            setAssignNewMachine={setIsAssignNewMachine}
            modemId={modemDetailsResponse?.modemId}
            refetchModemDetails={refetch}
          />
        )}
        {!modemDetailsResponse?.asset && !isAssignNewMachine && (
          <NoContent
            icon={<FactoryOutlined className={classes.machineIcon} />}
            title="No Machine Associated"
            desc={
              <>
                There is no machine associated to this modem.
                <br />
                Please add new machine
              </>
            }
            buttonName="Assign New"
            onBtnClickHandler={() => {
              setIsAssignNewMachine(true);
            }}
          />
        )}
        {isAssignNewMachine && (
          <AssignNewAsset setAssignNewMachine={setIsAssignNewMachine} modemId={modemDetailsResponse} />
        )}
      </Box>
      <GridContainer
        data-testid="info-pad"
        display="flex"
        justifyContent="flex-start"
        alignItems="baseline"
        columnSpacing={1.5}
        rowSpacing={1}
      >
        <GridItem
          xs={12}
          sm={12}
          md={12}
          display="flex"
          sx={{
            minWidth: '133px',
            justifyContent: {
              xs: 'flex-start',
              sm: 'flex-end',
            },
          }}
        >
          <Button
            color={'error'}
            onClick={() => {
              setShowDeleteModal(true);
            }}
            data-testid="delete-modem"
          >
            {MESSAGE.DELETE_MODEM}
          </Button>
        </GridItem>
      </GridContainer>
      <ConfirmationModal
        key={'delete-modal'}
        isOpen={showDeleteModal}
        title={MESSAGE.CONFIRMATION}
        msg={MESSAGE.MODEM_DELETE_DESCRIPTION}
        buttons={[
          { text: MESSAGE.DELETE, value: MESSAGE.DELETE },
          { text: MESSAGE.CANCEL, value: MESSAGE.CANCEL },
        ]}
        onClick={onDeleteModalClickHandler}
      />
      {historyModal && (
        <Modal
          open={historyModal}
          onClose={handleCloseHistoryModal}
          title={'Modem History'}
          titleVariant="subtitle10"
          closeIcon={true}
          PaperProps={{
            sx: {
              width: '90%',
              maxWidth: 'none',
              maxHeight: '90%',
              height: '70%',
              overflow: 'hidden',
            },
          }}
        >
          {isHistoryLoading ? (
            <LoadingSpinner containerHeight={300} />
          ) : (
            <ModemHistoryModel modemId={selectedModemId} />
          )}
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ModemDetails;
